import {
  parsePhoneNumberFromString,
  type CountryCode,
  type MetadataJson,
} from "libphonenumber-js/core";
import rawMetadata from "libphonenumber-js/metadata.min.json";

const metadata = ((rawMetadata as { default?: MetadataJson }).default ??
  rawMetadata) as MetadataJson;

const ARABIC_INDIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";
const EASTERN_ARABIC_INDIC_DIGITS = "۰۱۲۳۴۵۶۷۸۹";

export const DEFAULT_PHONE_COUNTRY: CountryCode = "EG";

function mapArabicDigitToAscii(char: string): string {
  const arabicIndicIndex = ARABIC_INDIC_DIGITS.indexOf(char);
  if (arabicIndicIndex >= 0) {
    return String(arabicIndicIndex);
  }

  const easternArabicIndicIndex = EASTERN_ARABIC_INDIC_DIGITS.indexOf(char);
  if (easternArabicIndicIndex >= 0) {
    return String(easternArabicIndicIndex);
  }

  return char;
}

export function normalizeUnicodeDigits(value: string): string {
  return value
    .split("")
    .map((char) => mapArabicDigitToAscii(char))
    .join("");
}

function normalizePhoneCandidate(value: string): string {
  const normalizedDigits = normalizeUnicodeDigits(value);
  const cleaned = normalizedDigits
    .replace(/[\u200E\u200F\u061C\s()\-_.]/g, "")
    .replace(/(?!^)\+/g, "");

  return cleaned;
}

export function toE164OrNull(
  value: string,
  defaultCountry: CountryCode = DEFAULT_PHONE_COUNTRY,
): string | null {
  const candidate = normalizePhoneCandidate(value.trim());
  if (!candidate) {
    return null;
  }

  const phone = candidate.startsWith("+")
    ? parsePhoneNumberFromString(candidate, metadata)
    : parsePhoneNumberFromString(candidate, defaultCountry, metadata);

  if (!phone || !phone.isValid()) {
    return null;
  }

  return phone.number;
}

export function isPlausiblePhone(
  value: string,
  defaultCountry: CountryCode = DEFAULT_PHONE_COUNTRY,
): boolean {
  return toE164OrNull(value, defaultCountry) !== null;
}

export function normalizeIdentifierInput(
  value: string,
  defaultCountry: CountryCode = DEFAULT_PHONE_COUNTRY,
): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  if (trimmed.includes("@")) {
    return trimmed.toLowerCase();
  }

  const normalizedPhone = toE164OrNull(trimmed, defaultCountry);
  return normalizedPhone ?? normalizeUnicodeDigits(trimmed);
}
